import * as i0 from '@angular/core';
import { NgZone, ɵmarkDirty, Directive, Input, NgModule, Pipe } from '@angular/core';
import { isObservable, from, Observable, ReplaySubject, pipe, Subscription } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

function createRenderScheduler(config) {
    function schedule() {
        if (hasZone(config.ngZone)) {
            config.cdRef.markForCheck();
        }
        else {
            const context = getCdRefContext(config.cdRef);
            ɵmarkDirty(context);
        }
    }
    return { schedule };
}
/**
 * @description
 * Determines if the application uses `NgZone` or `NgNoopZone` as ngZone service instance.
 */
function hasZone(z) {
    return z instanceof NgZone;
}
function getCdRefContext(cdRef) {
    return cdRef.context;
}

function combineRenderEventHandlers(handlers) {
    return (event) => handlers[event.type]?.(event);
}

function fromPotentialObservable(potentialObservable) {
    if (isObservable(potentialObservable)) {
        return potentialObservable;
    }
    if (isPromiseLike(potentialObservable)) {
        return from(potentialObservable);
    }
    return new Observable((subscriber) => {
        subscriber.next(potentialObservable);
    });
}
function isPromiseLike(value) {
    return typeof value?.then === 'function';
}

function createRenderEventManager(handlers) {
    const handleRenderEvent = combineRenderEventHandlers(handlers);
    const potentialObservable$ = new ReplaySubject(1);
    return {
        nextPotentialObservable(potentialObservable) {
            potentialObservable$.next(potentialObservable);
        },
        handlePotentialObservableChanges() {
            return potentialObservable$.pipe(distinctUntilChanged(), switchMapToRenderEvent(), distinctUntilChanged(renderEventComparator), tap(handleRenderEvent));
        },
    };
}
function switchMapToRenderEvent() {
    return pipe(switchMap((potentialObservable) => {
        const observable$ = fromPotentialObservable(potentialObservable);
        let reset = true;
        return new Observable((subscriber) => {
            const subscription = observable$.subscribe({
                next(value) {
                    subscriber.next({ type: 'next', value, reset });
                    reset = false;
                },
                error(error) {
                    subscriber.next({ type: 'error', error, reset });
                    reset = false;
                },
                complete() {
                    subscriber.next({ type: 'complete', reset });
                    reset = false;
                },
            });
            if (reset) {
                subscriber.next({ type: 'suspense', reset });
                reset = false;
            }
            return subscription;
        });
    }));
}
function renderEventComparator(previous, current) {
    if (previous.type !== current.type || previous.reset !== current.reset) {
        return false;
    }
    if (current.type === 'next') {
        return previous.value === current.value;
    }
    if (current.type === 'error') {
        return previous.error === current.error;
    }
    return true;
}

/**
 * @ngModule LetModule
 *
 * @description
 *
 * The `*ngrxLet` directive serves a convenient way of binding observables to a view context
 * (DOM element's scope). It also helps with several internal processing under the hood.
 *
 * @usageNotes
 *
 * ### Displaying Observable Values
 *
 * ```html
 * <ng-container *ngrxLet="number$ as n">
 *   <app-number [number]="n"></app-number>
 * </ng-container>
 *
 * <ng-container *ngrxLet="number$; let n">
 *   <app-number [number]="n"></app-number>
 * </ng-container>
 * ```
 *
 * ### Tracking Different Observable Events
 *
 * ```html
 * <ng-container *ngrxLet="number$ as n; let e = $error; let c = $complete">
 *   <app-number [number]="n" *ngIf="!e && !c">
 *   </app-number>
 *
 *   <p *ngIf="e">There is an error: {{ e }}</p>
 *   <p *ngIf="c">Observable is completed.</p>
 * </ng-container>
 * ```
 *
 * ### Using Suspense Template
 *
 * ```html
 * <ng-container *ngrxLet="number$ as n; suspenseTpl: loading">
 *   <app-number [number]="n"></app-number>
 * </ng-container>
 *
 * <ng-template #loading>
 *   <p>Loading...</p>
 * </ng-template>
 * ```
 *
 * ### Using Aliases for Non-Observable Values
 *
 * ```html
 * <ng-container *ngrxLet="userForm.controls.email as email">
 *   <input type="text" [formControl]="email" />
 *
 *   <ng-container *ngIf="email.errors && (email.touched || email.dirty)">
 *     <p *ngIf="email.errors.required">This field is required.</p>
 *     <p *ngIf="email.errors.email">This field must be an email.</p>
 *   </ng-container>
 * </ng-container>
 * ```
 *
 * @publicApi
 */
class LetDirective {
    constructor(cdRef, ngZone, mainTemplateRef, viewContainerRef, errorHandler) {
        this.cdRef = cdRef;
        this.ngZone = ngZone;
        this.mainTemplateRef = mainTemplateRef;
        this.viewContainerRef = viewContainerRef;
        this.errorHandler = errorHandler;
        this.isMainViewCreated = false;
        this.isSuspenseViewCreated = false;
        this.viewContext = {
            $implicit: undefined,
            ngrxLet: undefined,
            $error: undefined,
            $complete: false,
            $suspense: true,
        };
        this.renderScheduler = createRenderScheduler({
            ngZone: this.ngZone,
            cdRef: this.cdRef,
        });
        this.renderEventManager = createRenderEventManager({
            suspense: () => {
                this.viewContext.$implicit = undefined;
                this.viewContext.ngrxLet = undefined;
                this.viewContext.$error = undefined;
                this.viewContext.$complete = false;
                this.viewContext.$suspense = true;
                this.renderSuspenseView();
            },
            next: (event) => {
                this.viewContext.$implicit = event.value;
                this.viewContext.ngrxLet = event.value;
                this.viewContext.$suspense = false;
                if (event.reset) {
                    this.viewContext.$error = undefined;
                    this.viewContext.$complete = false;
                }
                this.renderMainView();
            },
            error: (event) => {
                this.viewContext.$error = event.error;
                this.viewContext.$suspense = false;
                if (event.reset) {
                    this.viewContext.$implicit = undefined;
                    this.viewContext.ngrxLet = undefined;
                    this.viewContext.$complete = false;
                }
                this.renderMainView();
                this.errorHandler.handleError(event.error);
            },
            complete: (event) => {
                this.viewContext.$complete = true;
                this.viewContext.$suspense = false;
                if (event.reset) {
                    this.viewContext.$implicit = undefined;
                    this.viewContext.ngrxLet = undefined;
                    this.viewContext.$error = undefined;
                }
                this.renderMainView();
            },
        });
        this.subscription = new Subscription();
    }
    set ngrxLet(potentialObservable) {
        this.renderEventManager.nextPotentialObservable(potentialObservable);
    }
    static ngTemplateContextGuard(dir, ctx) {
        return true;
    }
    ngOnInit() {
        this.subscription.add(this.renderEventManager.handlePotentialObservableChanges().subscribe());
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    renderMainView() {
        if (this.isSuspenseViewCreated) {
            this.isSuspenseViewCreated = false;
            this.viewContainerRef.clear();
        }
        if (!this.isMainViewCreated) {
            this.isMainViewCreated = true;
            this.viewContainerRef.createEmbeddedView(this.mainTemplateRef, this.viewContext);
        }
        this.renderScheduler.schedule();
    }
    renderSuspenseView() {
        if (this.suspenseTemplateRef && this.isMainViewCreated) {
            this.isMainViewCreated = false;
            this.viewContainerRef.clear();
        }
        if (this.suspenseTemplateRef && !this.isSuspenseViewCreated) {
            this.isSuspenseViewCreated = true;
            this.viewContainerRef.createEmbeddedView(this.suspenseTemplateRef);
        }
        if (this.isMainViewCreated || this.isSuspenseViewCreated) {
            this.renderScheduler.schedule();
        }
    }
}
/** @nocollapse */ LetDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: LetDirective, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i0.ErrorHandler }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ LetDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.0-rc.1", type: LetDirective, selector: "[ngrxLet]", inputs: { ngrxLet: "ngrxLet", suspenseTemplateRef: ["ngrxLetSuspenseTpl", "suspenseTemplateRef"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: LetDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[ngrxLet]' }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i0.ErrorHandler }]; }, propDecorators: { ngrxLet: [{
                type: Input
            }], suspenseTemplateRef: [{
                type: Input,
                args: ['ngrxLetSuspenseTpl']
            }] } });

class LetModule {
}
/** @nocollapse */ LetModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: LetModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ LetModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0-rc.1", ngImport: i0, type: LetModule, declarations: [LetDirective], exports: [LetDirective] });
/** @nocollapse */ LetModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: LetModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: LetModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [LetDirective],
                    exports: [LetDirective],
                }]
        }] });

/**
 * @ngModule PushModule
 *
 * @description
 *
 * The `ngrxPush` pipe serves as a drop-in replacement for the `async` pipe.
 * It contains intelligent handling of change detection to enable us
 * running in zone-full as well as zone-less mode without any changes to the code.
 *
 * @usageNotes
 *
 * ```html
 * <p>{{ number$ | ngrxPush }}</p>
 *
 * <ng-container *ngIf="number$ | ngrxPush as n">{{ n }}</ng-container>
 *
 * <app-number [number]="number$ | ngrxPush"></app-number>
 * ```
 *
 * @publicApi
 */
class PushPipe {
    constructor(cdRef, ngZone, errorHandler) {
        this.cdRef = cdRef;
        this.ngZone = ngZone;
        this.errorHandler = errorHandler;
        this.renderScheduler = createRenderScheduler({
            ngZone: this.ngZone,
            cdRef: this.cdRef,
        });
        this.renderEventManager = createRenderEventManager({
            suspense: () => this.setRenderedValue(undefined),
            next: (event) => this.setRenderedValue(event.value),
            error: (event) => {
                if (event.reset) {
                    this.setRenderedValue(undefined);
                }
                this.errorHandler.handleError(event.error);
            },
            complete: (event) => {
                if (event.reset) {
                    this.setRenderedValue(undefined);
                }
            },
        });
        this.subscription = this.renderEventManager
            .handlePotentialObservableChanges()
            .subscribe();
    }
    transform(potentialObservable) {
        this.renderEventManager.nextPotentialObservable(potentialObservable);
        return this.renderedValue;
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    setRenderedValue(value) {
        if (value !== this.renderedValue) {
            this.renderedValue = value;
            this.renderScheduler.schedule();
        }
    }
}
/** @nocollapse */ PushPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: PushPipe, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i0.ErrorHandler }], target: i0.ɵɵFactoryTarget.Pipe });
/** @nocollapse */ PushPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.0.0-rc.1", ngImport: i0, type: PushPipe, name: "ngrxPush", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: PushPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'ngrxPush', pure: false }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i0.ErrorHandler }]; } });

class PushModule {
}
/** @nocollapse */ PushModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: PushModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ PushModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0-rc.1", ngImport: i0, type: PushModule, declarations: [PushPipe], exports: [PushPipe] });
/** @nocollapse */ PushModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: PushModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: PushModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PushPipe],
                    exports: [PushPipe],
                }]
        }] });

/**
 * @deprecated `ReactiveComponentModule` is deprecated in favor of `LetModule` and `PushModule`.
 * See the {@link https://ngrx.io/guide/migration/v14#reactivecomponentmodule migration guide}
 * for more information.
 */
class ReactiveComponentModule {
}
/** @nocollapse */ ReactiveComponentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: ReactiveComponentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ ReactiveComponentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0-rc.1", ngImport: i0, type: ReactiveComponentModule, exports: [LetModule, PushModule] });
/** @nocollapse */ ReactiveComponentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: ReactiveComponentModule, imports: [LetModule, PushModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0-rc.1", ngImport: i0, type: ReactiveComponentModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [LetModule, PushModule],
                }]
        }] });

/**
 * DO NOT EDIT
 *
 * This file is automatically generated at build
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LetDirective, LetModule, PushModule, PushPipe, ReactiveComponentModule };
